import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Plus, Sparkles } from "lucide-react";
import useSEO from "@/lib/useSEO";
import { PRICING_DISCLAIMER } from "@/data/content";
import { usePublicSettings } from "@/contexts/SettingsContext";
import { usePublicServices, iconForService } from "@/contexts/ServicesContext";

export default function Services() {
  const settings = usePublicSettings();
  const { services, loading } = usePublicServices();
  useSEO({
    slug: "services",
    title: "Cleaning Services in Sacramento | P & A Cleaning",
    description:
      "Move-in, move-out, pre-listing, property management, recurring, commercial, junk removal, carpet cleaning, and yard cleanup in Sacramento.",
  });

  return (
    <div data-testid="page-services">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#F8FAFC] to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#1E3A8A] shadow-soft">
            <Sparkles className="w-3.5 h-3.5 text-[#34D399]" /> Sacramento, CA
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-[#0F172A] tracking-tight">
            Comprehensive <span className="gradient-text">Cleaning Solutions</span>
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-lg text-[#475569]">
            From quick refreshes to full property turnovers — see what we offer
            and pick the right fit. Every job gets a custom per-job estimate.
          </p>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="py-12 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {loading && services.length === 0 ? (
            <div className="text-center text-sm text-[#475569]" data-testid="services-loading">
              Loading services…
            </div>
          ) : (
            services.map((s) => {
              const Icon = iconForService(s);
              return (
                <article
                  key={s.id || s.slug}
                  data-testid={`services-row-${s.slug}`}
                  className="bg-[#F8FAFC] rounded-3xl p-6 md:p-10 border border-slate-100"
                >
                  <div className="grid md:grid-cols-12 gap-8 items-start">
                    <div className="md:col-span-5">
                      {/* Icon + title on the same row */}
                      <div className="flex items-center gap-4">
                        <span className="w-12 h-12 rounded-2xl bg-white border border-slate-100 flex items-center justify-center shadow-soft flex-shrink-0">
                          <Icon className="w-6 h-6 text-[#1E3A8A]" strokeWidth={2} />
                        </span>
                        <h2
                          data-testid={`services-row-${s.slug}-title`}
                          className="text-2xl sm:text-3xl font-display font-semibold text-[#0F172A] leading-tight"
                        >
                          {s.learn_more_url ? (
                            <Link
                              to={s.learn_more_url}
                              data-testid={`services-row-${s.slug}-title-link`}
                              className="hover:text-[#1E3A8A] transition-colors"
                            >
                              {s.name}
                            </Link>
                          ) : (
                            s.name
                          )}
                        </h2>
                      </div>
                      <p
                        data-testid={`services-row-${s.slug}-short`}
                        className="mt-4 text-[#475569] leading-relaxed"
                      >
                        {s.short_description}
                      </p>
                      {s.price_range && (
                        <p className="mt-3 text-sm font-semibold text-[#1E3A8A]">
                          {s.quote_required ? "Pricing: " : "Estimated price: "}
                          <span className="font-normal text-[#475569]">{s.price_range}</span>
                        </p>
                      )}
                      <div className="mt-6 flex flex-wrap items-center gap-3">
                        <Link
                          to={s.estimate_cta_url || "/request-estimate"}
                          data-testid={`services-row-${s.slug}-cta`}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF] transition-colors"
                        >
                          Request Estimate <ArrowRight className="w-4 h-4" />
                        </Link>
                        {s.learn_more_url ? (
                          <Link
                            to={s.learn_more_url}
                            data-testid={`services-row-${s.slug}-learnmore`}
                            aria-label={`Learn more about ${s.name}`}
                            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#1E3A8A] text-[#1E3A8A] font-semibold hover:bg-[#1E3A8A]/5 transition-colors"
                          >
                            Learn More <ArrowRight className="w-4 h-4" aria-hidden="true" />
                          </Link>
                        ) : null}
                      </div>
                    </div>

                    {s.full_description && (
                      <div className="md:col-span-7">
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#0F172A] mb-3">
                          What's included
                        </p>
                        <p
                          data-testid={`services-row-${s.slug}-full`}
                          className="text-sm text-[#0F172A] leading-relaxed whitespace-pre-line"
                        >
                          {s.full_description}
                        </p>
                        {s.category && (
                          <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#7C3AED] bg-[#A78BFA]/10 px-2.5 py-1 rounded-full">
                            <Plus className="w-3 h-3" />
                            {s.category}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </article>
              );
            })
          )}

          {/* Special requests CTA — static, not a service record */}
          <article
            data-testid="services-row-special"
            className="bg-gradient-to-br from-[#1E3A8A] to-[#8B5CF6] rounded-3xl p-8 md:p-12 text-white"
          >
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-white" strokeWidth={2} />
              </span>
              <h2 className="text-2xl sm:text-3xl font-display font-semibold">
                Special Cleaning Requests
              </h2>
            </div>
            <p className="mt-4 max-w-2xl text-white/85 leading-relaxed">
              Have a unique project, a tight deadline, or a combination of
              services? Send the details and we'll put together a custom quote.
            </p>
            <Link
              to="/request-estimate"
              data-testid="services-special-cta"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 transition-colors"
            >
              Request Estimate <ArrowRight className="w-4 h-4" />
            </Link>
          </article>

          <p className="text-xs text-[#475569] leading-relaxed bg-white border border-slate-100 rounded-2xl p-5" data-testid="services-pricing-disclaimer">
            <strong className="text-[#0F172A]">Pricing note:</strong>{" "}
            {settings.pricing_disclaimer || PRICING_DISCLAIMER}
          </p>
        </div>
      </section>
    </div>
  );
}
